import {ReactElement, useRef} from "react"
import {ItemWarehouse} from "./api/itemwarehouse"
import {PASOverviewTableRow} from "./PASOverviewTableRow"
import {ISODate} from "./date"
import {FullScreenButton} from "./FullScreenButton"
import "./PASOverviewTable.scss"


type FakeItemWarehouse = {itemCode: string, warehouseCode: string}
const cache: Map<string, Map<string, FakeItemWarehouse>> = new Map()
function getFakeItemWarehouse(
  itemCode: string, warehouseCode: string): FakeItemWarehouse {
  // gets a cached FakeIW so that the same values always result in the same object
  if (!cache.has(itemCode)) {
    cache.set(itemCode, new Map())
  }
  let perItem = cache.get(itemCode)!
  if (!perItem.has(warehouseCode)) {
    perItem.set(warehouseCode, {itemCode, warehouseCode})
  }
  return perItem.get(warehouseCode)!
}

function parseInt10WithDefault(s: string | undefined, defaultValue: number): number {
  const n = parseInt(s ?? "")
  return isNaN(n) ? defaultValue : n
}

type Props = {
  lines: string[],
  warehouseCode: string
  itemWarehouseByItemCode: Map<string, ItemWarehouse>
  negativeToOOS: boolean
  showCounts: boolean
}
export function PASOverviewTable(
  {lines, warehouseCode, itemWarehouseByItemCode,
    negativeToOOS, showCounts}: Props
): ReactElement {
  const thisTable = useRef<HTMLDivElement>(null)

  const today = ISODate.localToday()
  const tableRowKeys = new Set<string>()
  return <div ref={thisTable}>
      <FullScreenButton elementToFullScreen={thisTable} />
      <table className="PASOverviewTable">
      <thead>
        <tr>
          <th />
          <th />
          <th />
          <th className="out-of-stock">Out of stock</th>
        </tr>
        <tr>
          <th>{today.toISOWeekString()}</th>
          <th />
          <th />
          <th className="warning">
            Less than {ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL}
          </th>
        </tr>
        <tr>
          <th>{today.toDDMMYYYYString()}</th>
          <th />
          <th />
          <th className="info">
            {ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL} to
            {ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL}
          </th>
        </tr>
        <tr>
          <th />
          <th />
          <th />
          <th className="ok">
            More than {ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL}
          </th>
        </tr>
        <tr className="emptyLine" />
        <tr>
          <th />
          <th />
          <th />
          <th>Stock level</th>
          <th />
          <th>Available again</th>
        </tr>
        <tr className="emptyLine" />
      </thead>
      <tbody>
        {
          lines.map(line => line.trim()).map(line => {
            let key = line
            for (let i=0; ; i++) {
              if (!tableRowKeys.has(key)) {
                break
              }
              key = `${line}_${i}`
            }

            const lineparts = line.split(/([,\t])/)

            tableRowKeys.add(key)
            if (lineparts.every(p => p === "")) {
              return <tr key={key} className="emptyLine" />
            }
            if (lineparts[0].endsWith(":")
                  && lineparts.slice(1).every(p => p === "")) {
              return <tr key={key} className="header">
                <th colSpan={2}>{line.slice(0, -1)}</th>
              </tr>
            }
            if ([2, 4].map(i => lineparts[i]).some(p => !/^[0-9]*$/.test(p ?? ""))) {
              return <tr key={key} className="error">
                <td colSpan={6}>Error in line: <i>{line}</i></td>
              </tr>
            }
            const itemCode = lineparts[0]
            const warningLevel = parseInt10WithDefault(
              lineparts[2], ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL)
            const infoLevel = parseInt10WithDefault(
              lineparts[4], ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL)
            const comment = lineparts.slice(6).join("")

            const itemWarehouse =
              itemWarehouseByItemCode.get(itemCode) ?? getFakeItemWarehouse(
                itemCode, warehouseCode)
            return <PASOverviewTableRow key={key}
              itemWarehouse={itemWarehouse} infoLevel={infoLevel}
              warningLevel={warningLevel} negativeToOOS={negativeToOOS}
              showCounts={showCounts} comment={comment}/>
          })
        }
      </tbody>
    </table>
  </div>

}
